import type {
	ClassificationCode,
	ClassificationInfo,
	MockProfile,
	OccurrenceType,
} from '@/types/occurrence'

/** Persona exibida para cada perfil simulado. */
export const PERSONA: Record<MockProfile, { name: string; scope: string }> = {
	ADMIN: { name: 'Carlos Admin', scope: 'Corporativo (sem acesso clínico)' },
	SSMA_MANAGER: { name: 'Jonathan Carboni Henck', scope: 'Rio de Janeiro' },
	MANAGER: { name: 'Patrícia Gestora', scope: 'Rio de Janeiro · Vale S.A' },
	TECHNICIAN: { name: 'Luana Ventura Alves Ferreira', scope: 'Rio de Janeiro' },
	MEDICINE: { name: 'Dra. Helena Medicina', scope: 'Rio de Janeiro' },
}

export const BRANCHES = [
	'Rio de Janeiro',
	'SP Interior',
	'Grande SP',
	'Nordeste',
	'Minas Gerais',
]

export const CONTRACTS: Record<string, string[]> = {
	'Rio de Janeiro': ['Vale S.A - Tig - Acessos', 'Teste'],
	'SP Interior': ['RHODIA'],
	'Grande SP': ['Mercedes Benz Serviços'],
	Nordeste: ['BRASKEM AL - CS1 / PVC2', 'RNEST PINTURA'],
	'Minas Gerais': ['Vale - Eletr.'],
}

export const JOB_ROLES = [
	'Mont Andaime',
	'Pintor Industrial',
	'Soldador',
	'Encarregado',
	'Ajudante',
	'Técnico de Segurança',
	'Isolador Térmico',
	'Refratarista',
]

export const BODY_PARTS = [
	'NA',
	'Rosto',
	'Braço',
	'Mão',
	'Dedo',
	'Perna',
	'Pé',
	'Tronco',
	'Olho',
	'Cabeça',
]

export const WORK_TYPES = [
	'Manutenção',
	'Parada',
	'Obra nova',
	'Operação assistida',
]
export const SERVICE_TYPES = [
	'Acessos',
	'Refratário',
	'Pintura',
	'Isolamento',
	'Soldagem',
]
export const FREQUENCIES = ['Rotineira', 'Eventual', 'Não planejada']
export const EDUCATION = [
	'Ensino fundamental incompleto',
	'Ensino fundamental completo',
	'Ensino médio completo',
	'Ensino técnico',
	'Ensino superior',
]
export const LIFE_ATTITUDES = [
	'NA',
	'Não realizo atividades sob efeito de álcool ou drogas',
	'Uso corretamente os EPIs',
	'Não trabalho em altura sem proteção',
	'Respeito a sinalização e bloqueios',
]
export const CAUSAL_LINKS = [
	'Relacionado ao trabalho',
	'Não relacionado ao trabalho',
	'Em investigação',
]

export const PEOPLE = [
	{
		badge: '911540',
		name: 'Max William Eneas Silva',
		jobRole: 'Mont Andaime',
		admission: '2025-06-25',
		age: 46,
		education: 'Ensino fundamental completo',
	},
	{
		badge: '912201',
		name: 'João da Silva Santos',
		jobRole: 'Pintor Industrial',
		admission: '2023-02-10',
		age: 38,
		education: 'Ensino médio completo',
	},
	{
		badge: '913004',
		name: 'Marcos Antônio Pereira',
		jobRole: 'Soldador',
		admission: '2021-08-02',
		age: 41,
		education: 'Ensino técnico',
	},
	{
		badge: '914777',
		name: 'Rafael Souza Lima',
		jobRole: 'Ajudante',
		admission: '2026-01-12',
		age: 24,
		education: 'Ensino médio completo',
	},
	{
		badge: '915320',
		name: 'Wagner Oliveira',
		jobRole: 'Encarregado',
		admission: '2018-04-20',
		age: 52,
		education: 'Ensino médio completo',
	},
]

export const RESPONSIBLES = [
	'Ventura Alves Ferreira, Luana',
	'Patrícia Gestora',
	'Wagner Oliveira',
	'Jonathan Carboni Henck',
	'Rafael Souza Lima',
]
export const APPROVERS = ['Patrícia Gestora', 'Jonathan Carboni Henck']

export const MEDICAL_SUBJECTS = [
	'Análise do Atestado Médico',
	'ASO',
	'Certidão de Óbito',
	'Exame especialista',
	'Foto da lesão',
	'Reaproveitamento profissional',
	'Relatório de retorno às atividades',
	'Relatório médico',
	'Retorno ao trabalho',
]

export const DOC_CATEGORIES = [
	'CAT',
	'Laudo',
	'Relatório de investigação',
	'APR / Procedimento',
	'Treinamento',
	'Outros',
]

export const ISHIKAWA_CATEGORIES = [
	'Método',
	'Máquina',
	'Material',
	'Mão de obra',
	'Meio ambiente',
	'Medição',
]

export const CLASSIFICATIONS: Record<ClassificationCode, ClassificationInfo> = {
	FA: {
		code: 'FA',
		label: 'FA — Fatalidade',
		definition:
			'Evento que resulta em óbito de empregado ou terceiro em atividade da empresa.',
		criteria:
			'Óbito confirmado, independentemente do tempo decorrido entre o evento e o falecimento.',
		examples:
			'Queda de altura com óbito no local; soterramento com óbito no hospital.',
		notApplicable:
			'Mal súbito sem relação com o trabalho comprovada (avaliar com a Medicina).',
		guidingQuestions: [
			'Houve óbito?',
			'O óbito decorreu do evento?',
			'A Medicina confirmou o nexo?',
		],
	},
	CAF: {
		code: 'CAF',
		label: 'CAF — Com afastamento',
		definition:
			'Acidente com lesão que impede o empregado de retornar ao trabalho no dia seguinte.',
		criteria: 'Afastamento superior ao dia do acidente, atestado por médico.',
		examples: 'Fratura com 15 dias de afastamento.',
		notApplicable: 'Lesões tratadas com primeiros socorros sem afastamento.',
		guidingQuestions: [
			'Há atestado de afastamento?',
			'O retorno foi no dia seguinte?',
		],
	},
	SAF_RE: {
		code: 'SAF_RE',
		label: 'SAF/RE — Sem afastamento com restrição',
		definition:
			'Acidente sem afastamento em que o empregado retorna com restrição de atividades.',
		criteria: 'Restrição médica formal de atividades.',
		examples: 'Retorno ao trabalho sem poder levantar peso por 10 dias.',
		notApplicable: 'Retorno sem qualquer restrição (ver SAF/AC).',
		guidingQuestions: [
			'Há laudo de restrição?',
			'Qual a duração da restrição?',
		],
	},
	SAF: {
		code: 'SAF',
		label: 'SAF — Sem afastamento',
		definition:
			'Acidente com tratamento médico, sem afastamento nem restrição.',
		criteria: 'Tratamento médico além de primeiros socorros; retorno imediato.',
		examples: 'Sutura com retorno no mesmo turno.',
		notApplicable: 'Apenas primeiros socorros (ver PS).',
		guidingQuestions: [
			'Houve tratamento médico?',
			'Houve restrição ou afastamento?',
		],
	},
	PS: {
		code: 'PS',
		label: 'PS — Primeiros socorros',
		definition:
			'Lesão leve tratada com primeiros socorros, sem tratamento médico.',
		criteria: 'Tratamento limitado a curativos simples, limpeza, compressa.',
		examples: 'Escoriação superficial no rosto tratada com curativo.',
		notApplicable:
			'Qualquer necessidade de sutura, medicação prescrita ou encaminhamento.',
		guidingQuestions: [
			'Foi necessário atendimento médico?',
			'Houve prescrição de medicamento?',
		],
	},
	QA: {
		code: 'QA',
		label: 'QA — Quase acidente',
		definition: 'Evento sem lesão nem dano, mas com potencial para causá-los.',
		criteria: 'Ausência de lesão e dano; potencial real demonstrável.',
		examples: 'Ferramenta caiu em área isolada, sem atingir pessoas.',
		notApplicable: 'Eventos com qualquer lesão (ver PS/SAF).',
		guidingQuestions: [
			'Alguém poderia ter sido atingido?',
			'Houve dano material?',
		],
	},
	TRAJETO_CAF: {
		code: 'TRAJETO_CAF',
		label: 'Trajeto CAF',
		definition: 'Acidente de trajeto (casa-trabalho) com afastamento.',
		criteria: 'Percurso habitual, em horário compatível, com afastamento.',
		examples: 'Colisão no caminho para o trabalho com afastamento de 20 dias.',
		notApplicable: 'Desvio de percurso por motivo pessoal.',
		guidingQuestions: ['Era o trajeto habitual?', 'Há afastamento?'],
	},
	TRAJETO_SAF: {
		code: 'TRAJETO_SAF',
		label: 'Trajeto SAF',
		definition: 'Acidente de trajeto sem afastamento.',
		criteria: 'Percurso habitual, sem afastamento.',
		examples: 'Queda de moto no trajeto, retorno no dia seguinte.',
		notApplicable: 'Desvio de percurso por motivo pessoal.',
		guidingQuestions: ['Era o trajeto habitual?'],
	},
	QA_TRAJETO: {
		code: 'QA_TRAJETO',
		label: 'QA Trajeto',
		definition: 'Quase acidente durante o trajeto.',
		criteria: 'Sem lesão nem dano, durante o trajeto.',
		examples: 'Colisão evitada no caminho do transporte da empresa.',
		notApplicable: 'Eventos com lesão.',
		guidingQuestions: ['Era transporte da empresa?'],
	},
	DOENCA: {
		code: 'DOENCA',
		label: 'Doença ocupacional',
		definition: 'Doença produzida ou desencadeada pelo exercício do trabalho.',
		criteria: 'Nexo causal estabelecido pela Medicina do Trabalho.',
		examples: 'LER/DORT por movimento repetitivo.',
		notApplicable: 'Doença degenerativa sem nexo.',
		guidingQuestions: ['Há laudo com nexo causal?'],
	},
	DANO_MATERIAL: {
		code: 'DANO_MATERIAL',
		label: 'Dano material',
		definition:
			'Evento com dano a equipamento, veículo ou patrimônio, sem lesão a pessoas.',
		criteria: 'Dano comprovado a bem material.',
		examples: 'Colisão de veículo da empresa em manobra.',
		notApplicable: 'Eventos com lesão (classificar pelo dano à pessoa).',
		guidingQuestions: ['Há valor estimado do dano?', 'Houve lesão a pessoas?'],
	},
	SITUACAO_INSEGURA: {
		code: 'SITUACAO_INSEGURA',
		label: 'Situação insegura',
		definition:
			'Condição ou comportamento com potencial de gerar acidente, sem evento.',
		criteria: 'Condição observada que desvia do padrão.',
		examples: 'Proteção removida de equipamento.',
		notApplicable: 'Quando já houve evento (ver QA).',
		guidingQuestions: ['Houve evento ou apenas condição?'],
	},
	NAO_ACIDENTE: {
		code: 'NAO_ACIDENTE',
		label: 'Não acidente',
		definition:
			'Ocorrência registrada que, após análise, não configura acidente.',
		criteria: 'Sem relação com a atividade ou sem lesão/dano.',
		examples: 'Mal-estar sem nexo com o trabalho.',
		notApplicable: 'Qualquer evento com nexo comprovado.',
		guidingQuestions: ['Há relação com o trabalho?'],
	},
}

/** Classificações aplicáveis por tipo (hierarquia tipo → classificação). */
export const CLASSIFICATIONS_BY_TYPE: Record<
	OccurrenceType,
	ClassificationCode[]
> = {
	WORK_ACCIDENT: [
		'FA',
		'CAF',
		'SAF_RE',
		'SAF',
		'PS',
		'TRAJETO_CAF',
		'TRAJETO_SAF',
	],
	INCIDENT: ['QA', 'QA_TRAJETO', 'NAO_ACIDENTE'],
	NEAR_MISS: ['QA', 'QA_TRAJETO'],
	SSMA_DEVIATION: ['SITUACAO_INSEGURA'],
	RNC: ['NAO_ACIDENTE'],
	GOVERNANCE_DEVIATION: ['NAO_ACIDENTE'],
	OCCUPATIONAL_DISEASE: ['DOENCA'],
	PROPERTY_DAMAGE: ['DANO_MATERIAL'],
	ENVIRONMENTAL: ['NAO_ACIDENTE'],
}

/** Rótulo curto do potencial para listas (o texto completo fica em POTENTIAL_INFO). */
export const POTENTIAL_SHORT: Record<string, string> = {
	N1: 'Fatalidade',
	N2: 'Grave, com potencial fatal',
	N3: 'Leve, com potencial fatal',
	N4: 'Afastamento, sem potencial fatal',
	N5: 'Leve, sem potencial fatal',
}

export const POTENTIAL_INFO: Record<string, string> = {
	N1: 'Fatalidade(s)',
	N2: 'Eventos reais de vida mudada ou eventos com restrições/afastamento COM potencial de fatalidade',
	N3: 'Tratamento médico, primeiros socorros e outros eventos COM potencial de fatalidade',
	N4: 'Afastamentos ou restrições SEM potencial de fatalidade',
	N5: 'Primeiros socorros, tratamento médico e outros eventos SEM potencial de fatalidade',
}
